import { Alert, Flex, Form, Input, Radio, Select, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@/components/card/card';
import { Button } from '@/components/button/button';
import {
  useAfterHoursSettings,
  useUpdateAfterHoursSettings,
} from '@/hooks/use-company/use-company';
import {
  AFTER_HOURS_STRATEGIES,
  AfterHoursSettings as Settings,
  AfterHoursStrategy,
} from '@/services/company-service/company-service.types';
import { apiFieldErrors } from '@/utils/api-field-errors';
import { openNotification } from '@/utils/notification/notification';
import { isBlankPhone, phoneRule } from '@/utils/phone';
import styles from './after-hours-settings.module.scss';
import { timeZoneOptions } from './after-hours-settings.utils';

const NEEDS_VOICEMAIL: AfterHoursStrategy[] = ['voicemail', 'voicemail_with_targets'];

type AfterHoursSettingsProps = {
  companyName: string;
  onTimeZoneSaved?: (timeZone: string) => void;
};

export function AfterHoursSettings({ companyName, onTimeZoneSaved }: AfterHoursSettingsProps) {
  const { t } = useTranslation(['businessHours', 'common']);
  const [form] = Form.useForm<Settings>();
  const { settings, isLoading, error } = useAfterHoursSettings(companyName);
  const { updateAfterHoursSettings, isPending } = useUpdateAfterHoursSettings(companyName);

  useEffect(() => {
    if (settings) form.setFieldsValue(settings);
  }, [settings, form]);

  const zones = useMemo(() => timeZoneOptions(settings?.time_zone), [settings?.time_zone]);

  const handleFinish = async (values: Settings) => {
    const company: Settings = {
      ...values,
      after_hours_voicemail_number: isBlankPhone(values.after_hours_voicemail_number)
        ? ''
        : values.after_hours_voicemail_number,
    };
    try {
      const saved = await updateAfterHoursSettings({ company_name: companyName, company });
      if (saved.time_zone !== settings?.time_zone) onTimeZoneSaved?.(saved.time_zone);
    } catch (saveError) {
      const fields = apiFieldErrors(saveError);
      if (fields?.time_zone || fields?.after_hours_voicemail_number) {
        form.setFields([
          ...(fields.time_zone
            ? [{ name: 'time_zone' as const, errors: [t('afterHours.validation.timeZone')] }]
            : []),
          ...(fields.after_hours_voicemail_number
            ? [
                {
                  name: 'after_hours_voicemail_number' as const,
                  errors: [t('afterHours.validation.phoneInvalid')],
                },
              ]
            : []),
        ]);
        return;
      }
      openNotification({ message: t('afterHours.saveFailed'), type: 'error' });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  if (error || !settings) {
    return <Alert type="error" showIcon message={t('afterHours.loadError')} />;
  }

  return (
    <Card title={t('afterHours.title')} hint={t('afterHours.hint')}>
      <Form form={form} layout="vertical" onFinish={handleFinish} initialValues={settings}>
        <Form.Item
          label={t('afterHours.timeZone.label')}
          name="time_zone"
          extra={t('afterHours.timeZone.help')}
          rules={[{ required: true, message: t('afterHours.validation.timeZone') }]}
        >
          <Select showSearch options={zones} className={styles.timeZone} />
        </Form.Item>

        <Form.Item label={t('afterHours.strategy.label')} name="after_hours_strategy">
          <Radio.Group className={styles.radioGroup}>
            {AFTER_HOURS_STRATEGIES.map((value) => (
              <Radio key={value} value={value}>
                <div>
                  <strong>{t(`afterHours.strategies.${value}.label`)}</strong>
                  <p className={styles.optionDescription}>
                    {t(`afterHours.strategies.${value}.description`)}
                  </p>
                </div>
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={t('afterHours.voicemail.label')}
          name="after_hours_voicemail_number"
          extra={t('afterHours.voicemail.help')}
          dependencies={['after_hours_strategy']}
          rules={[
            ({ getFieldValue }) => ({
              validator: (_, value: string | undefined) =>
                NEEDS_VOICEMAIL.includes(getFieldValue('after_hours_strategy')) &&
                isBlankPhone(value)
                  ? Promise.reject(new Error(t('afterHours.validation.voicemailRequired')))
                  : Promise.resolve(),
            }),
            phoneRule(t('afterHours.validation.phoneInvalid'), { optional: true }),
          ]}
        >
          <Input placeholder="+48 123 456 789" className={styles.voicemail} />
        </Form.Item>

        <Flex>
          <Button buttonVariant="primary" onClick={() => form.submit()} loading={isPending}>
            {t('common:actions.save')}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
}
